/**
 * Minimal, zero-dependency i18n for FamFeel.
 *
 * Design goals (deliberately different from react-i18next et al):
 *   1. NO massive central dictionary. Strings live *next to the JSX*
 *      that uses them. Adding a Danish translation means adding a
 *      `da:` field to the same object you're already reading.
 *   2. Auto-detect Danish browsers on first visit, then persist the
 *      user's explicit choice in localStorage.
 *   3. Two locales for now (`en`, `da`). Extensible — add a locale
 *      key to any `t({...})` call and the fallthrough handles it.
 *   4. Sets <html lang> so screen readers, search engines and
 *      Chrome's translate prompt behave correctly.
 *
 * Usage:
 *   import { useI18n } from "@/lib/i18n";
 *   const { t, lang, setLang } = useI18n();
 *   <h1>{t({ en: "Everything, on one page", da: "Alt, på én side" })}</h1>
 *
 * Fallback rules (in `t`):
 *   dict[lang]  →  dict.en  →  dict.da  →  first available value  →  ""
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "ff_locale";
const SUPPORTED = ["en", "da"];
const DEFAULT_LOCALE = "en";

function detectInitialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {
    /* localStorage blocked (Safari private mode) — silent fallback */
  }
  const nav = (
    (typeof navigator !== "undefined" && (navigator.language || (navigator.languages || [])[0])) ||
    ""
  )
    .toString()
    .toLowerCase();
  if (nav.startsWith("da")) return "da";
  return DEFAULT_LOCALE;
}

const I18nContext = createContext({
  lang: DEFAULT_LOCALE,
  setLang: () => {},
  t: (dict) => (dict && (dict.en ?? "")) || "",
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLocale);

  // Reflect the current locale on <html lang> so the browser's own
  // "Translate this page?" prompt correctly identifies the page's
  // primary language (and so screen readers pick the right voice).
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) next = DEFAULT_LOCALE;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage blocked — user's choice won't persist, that's fine */
    }
  }, []);

  const t = useCallback(
    (dict) => {
      if (dict == null) return "";
      if (typeof dict === "string") return dict;
      if (dict[lang] != null) return dict[lang];
      if (dict.en != null) return dict.en;
      // Last-ditch: return the first present key so we never render
      // undefined / "[object Object]" in production.
      for (const k of SUPPORTED) if (dict[k] != null) return dict[k];
      const first = Object.values(dict)[0];
      return first != null ? first : "";
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

/** Small helper for components that only need the picker's active
 *  code (e.g. to fetch a Danish blog post). */
export function useLocaleCode() {
  return useContext(I18nContext).lang;
}
